import { Form, Formik, FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import * as bd from 'react-basic-design';
import { CategoryDTO } from '../../../api/category-api';
import { apiConfig } from '../../../api/config';
import { ProductDTO } from '../../../api/product-api';
import {
    ProductFeatureDTO,
    useDeleteProductFeatureApi,
    useSaveProductFeatureApi,
} from '../../../api/product-feature-api';
import {
    ProductSpecificationDTO,
    useDeleteProductSpecificationApi,
    useSaveProductSpecificationApi,
} from '../../../api/product-specification-api';
import {
    ProductPostPurchaseFeatureDTO,
    useDeleteProductPostPurchaseFeatureApi,
    useSaveProductPostPurchaseFeatureApi,
} from '../../../api/product-post-purchase-feature-api';
import SvgAdd from '../../../assets/icons/Add';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T, translate } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikCellToggle, FormikFileUpload, FormikTextField } from '../../../components/formik';
import { notify } from '../../../components/basic/notify';
import { SaveProductSpecificationModal } from '../product-specification-page/save-product-specification-modal';
import { SaveProductPostPurchaseFeatureModal } from './save-product-post-purchase-feature-modal';
import FormikTextEditor from '../../../components/formik/formik-text-editor';

interface SaveProductModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedProduct?: ProductDTO;
    categories: CategoryDTO[];
    onHide: () => void;
    onSave: (values: ProductDTO) => void;
    onDelete?: (product: ProductDTO) => void;
}

export function SaveProductModal({
    show,
    allowEdit,
    selectedProduct,
    categories,
    onHide,
    onSave,
    onDelete,
}: SaveProductModalProps) {
    const [newFeatureText, setNewFeatureText] = useState('');
    const [showSpecificationModal, setShowSpecificationModal] = useState(false);
    const [showPostPurchaseFeatureModal, setShowPostPurchaseFeatureModal] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>();
    const saveProductFeatureApi = useSaveProductFeatureApi();
    const deleteProductFeatureApi = useDeleteProductFeatureApi();
    const saveProductSpecificationApi = useSaveProductSpecificationApi();
    const deleteProductSpecificationApi = useDeleteProductSpecificationApi();
    const saveProductPostPurchaseFeatureApi = useSaveProductPostPurchaseFeatureApi();
    const deleteProductPostPurchaseFeatureApi = useDeleteProductPostPurchaseFeatureApi();

    const ProductSchema = yup.object().shape({
        name: yup.string().required(' '),
    });

    const defaultValues: ProductDTO = {
        id: selectedProduct?.id,
        name: selectedProduct ? selectedProduct.name : '',
        description: selectedProduct ? selectedProduct.description : '',
        enable: selectedProduct?.enable || false,
        file: new File([], ''),
        categoryCode: selectedProduct?.categoryCode || '',
        features: selectedProduct?.features || [],
        specifications: selectedProduct?.specifications || [],
        postPurchaseFeatures: selectedProduct?.postPurchaseFeatures || [],
    };

    const handleSaveSpecification = (
        specification: ProductSpecificationDTO,
        formik: FormikProps<ProductDTO>
    ) => {
        saveProductSpecificationApi.post(
            null,
            { productId: selectedProduct?.id, key: specification.key, value: specification.value },
            (data: any) => {
                formik.setFieldValue('specifications', data.specifications);
                setShowSpecificationModal(false);
            },
            notify.error
        );
    };

    const insertMode = !selectedProduct;

    useEffect(() => {
        if (!selectedProduct || !selectedProduct.attachmentId) setImageUrl(undefined);
        else setImageUrl(apiConfig.productUrl + `/image/${selectedProduct.id}`);
    }, [selectedProduct]);

    const handleDeleteFeature = (featureId: number, formik: FormikProps<ProductDTO>) => {
        deleteProductFeatureApi.remove(
            { id: featureId },
            () => {
                const newFeatures = formik.values.features.filter(f => f.id !== featureId);
                formik.setFieldValue('features', newFeatures);
            },
            notify.error
        );
    };

    const handleAddFeature = (formik: FormikProps<ProductDTO>) => {
        if (!newFeatureText) return;
        saveProductFeatureApi.post(
            null,
            {
                productId: formik.values.id,
                value: newFeatureText,
            },
            (data: any) => {
                formik.setFieldValue('features', data.productFeatures);
                setNewFeatureText('');
            },
            notify.error
        );
    };

    const handleDeleteSpecification = (
        specification: ProductSpecificationDTO,
        formik: FormikProps<ProductDTO>
    ) => {
        deleteProductSpecificationApi.remove(
            { id: specification.id },
            () => {
                formik.setFieldValue(
                    'specifications',
                    formik.values.specifications.filter(
                        (s: ProductSpecificationDTO) => s.id !== specification.id
                    )
                );
            },
            notify.error
        );
    };

    const handleSavePostPurchaseFeature = (
        postPurchaseFeature: ProductPostPurchaseFeatureDTO,
        formik: FormikProps<ProductDTO>
    ) => {
        saveProductPostPurchaseFeatureApi.post(
            null,
            {
                productId: selectedProduct?.id,
                value: postPurchaseFeature.value,
                link: postPurchaseFeature.link || '',
            },
            (data: any) => {
                formik.setFieldValue('postPurchaseFeatures', data.productPostPurchaseFeatures);
                setShowPostPurchaseFeatureModal(false);
            },
            notify.error
        );
    };

    const handleDeletePostPurchaseFeature = (
        postPurchaseFeatureId: number,
        formik: FormikProps<ProductDTO>
    ) => {
        deleteProductPostPurchaseFeatureApi.remove(
            { id: postPurchaseFeatureId },
            () => {
                const newPostPurchaseFeatures = formik.values.postPurchaseFeatures.filter(
                    f => f.id !== postPurchaseFeatureId
                );
                formik.setFieldValue('postPurchaseFeatures', newPostPurchaseFeatures);
            },
            notify.error
        );
    };

    if (!show) return <></>;

    return (
        <Formik initialValues={defaultValues} validationSchema={ProductSchema} onSubmit={onSave}>
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <bd.Flex align="center">
                                <T>{selectedProduct ? 'edit-product' : 'new-product'}</T>
                                <FormikCellToggle
                                    label={<T>enable</T>}
                                    name="enable"
                                    className="small"
                                    readOnly={!allowEdit}
                                />
                            </bd.Flex>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedProduct && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedProduct)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap className="mb-3">
                            <FormikTextField
                                label="name"
                                name="name"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                type="select"
                                items={[
                                    { id: '', title: '' },
                                    ...categories.map(x => ({ id: x.code, title: x.title })),
                                ]}
                                label="category"
                                name="categoryCode"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />
                        </bd.Flex>

                        {!insertMode && (
                            <>
                                <bd.FormRow label={translate('features')} className="w-100 mb-3">
                                    <bd.Flex vertical gap={2} className="w-100">
                                        <bd.TextField
                                            name="features"
                                            placeholder={translate('feature-title')}
                                            value={newFeatureText}
                                            readOnly={!allowEdit}
                                            width="11rem"
                                            onChange={e => setNewFeatureText(e.target.value)}
                                            buttonIcon={allowEdit && newFeatureText && <SvgAdd />}
                                            buttonDisabled={!allowEdit || !newFeatureText}
                                            buttonOnClick={() => handleAddFeature(formik)}
                                        />

                                        <bd.List compact className="flex-grow-1 min-w-100">
                                            {formik.values.features.map(
                                                (feature: ProductFeatureDTO, index: number) => (
                                                    <>
                                                        <bd.ListItem
                                                            compact
                                                            className="min-w-100"
                                                            key={feature.id}
                                                            variant="menu"
                                                            primary={
                                                                <bd.Flex
                                                                    className="text-muted min-w-100"
                                                                    gap={2}
                                                                >
                                                                    {index + 1}. {feature.value}
                                                                </bd.Flex>
                                                            }
                                                            alignTop
                                                            controls={
                                                                <bd.Button
                                                                    type="button"
                                                                    variant="icon"
                                                                    size="sm"
                                                                    color="secondary"
                                                                    onClick={() =>
                                                                        handleDeleteFeature(
                                                                            feature.id,
                                                                            formik
                                                                        )
                                                                    }
                                                                >
                                                                    <SvgDelete />
                                                                </bd.Button>
                                                            }
                                                        />
                                                        {index <
                                                            formik.values.features.length - 1 && (
                                                            <hr className="m-0 text-muted " />
                                                        )}
                                                    </>
                                                )
                                            )}
                                        </bd.List>
                                    </bd.Flex>
                                </bd.FormRow>

                                <bd.FormRow
                                    label={translate('specifications')}
                                    className="w-100 mb-3"
                                >
                                    <bd.Flex vertical gap={2} className="w-100">
                                        <div>
                                            <bd.Button
                                                disabled={!allowEdit}
                                                variant="outline"
                                                color="secondary"
                                                type="button"
                                                onClick={() => setShowSpecificationModal(true)}
                                            >
                                                <SvgAdd />
                                                <T>add-specification</T>
                                            </bd.Button>
                                        </div>
                                        <bd.List compact className="flex-grow-1">
                                            {formik.values.specifications.map(
                                                (
                                                    specification: ProductSpecificationDTO,
                                                    index: number
                                                ) => (
                                                    <>
                                                        <bd.ListItem
                                                            compact
                                                            variant="menu"
                                                            key={specification.key}
                                                            className="p-0"
                                                            inset={false}
                                                            primary={
                                                                <bd.Flex align="center" gap={2}>
                                                                    <b>{specification.key}</b>
                                                                    <span>:</span>
                                                                    <span className="text-muted">
                                                                        {specification.value}
                                                                    </span>
                                                                </bd.Flex>
                                                            }
                                                            controls={
                                                                <bd.Button
                                                                    type="button"
                                                                    variant="icon"
                                                                    size="sm"
                                                                    color="secondary"
                                                                    onClick={() =>
                                                                        handleDeleteSpecification(
                                                                            specification,
                                                                            formik
                                                                        )
                                                                    }
                                                                >
                                                                    <SvgDelete />
                                                                </bd.Button>
                                                            }
                                                        />
                                                        {index <
                                                            formik.values.specifications.length -
                                                                1 && (
                                                            <hr className="m-0 text-muted " />
                                                        )}
                                                    </>
                                                )
                                            )}
                                        </bd.List>
                                    </bd.Flex>
                                </bd.FormRow>

                                <bd.FormRow
                                    label={translate('post-purchase-features')}
                                    className="w-100 mb-3"
                                >
                                    <bd.Flex vertical gap={2} className="w-100">
                                        <div>
                                            <bd.Button
                                                disabled={!allowEdit}
                                                variant="outline"
                                                color="secondary"
                                                type="button"
                                                onClick={() => setShowPostPurchaseFeatureModal(true)}
                                            >
                                                <SvgAdd />
                                                <T>add-post-purchase-feature</T>
                                            </bd.Button>
                                        </div>
                                        <bd.List compact className="flex-grow-1 min-w-100">
                                            {formik.values.postPurchaseFeatures.map(
                                                (
                                                    postPurchaseFeature: ProductPostPurchaseFeatureDTO,
                                                    index: number
                                                ) => (
                                                    <>
                                                        <bd.ListItem
                                                            compact
                                                            className="min-w-100"
                                                            key={postPurchaseFeature.id}
                                                            variant="menu"
                                                            primary={
                                                                <bd.Flex
                                                                    className="text-muted min-w-100"
                                                                    vertical
                                                                    gap={1}
                                                                >
                                                                    <bd.Flex gap={2}>
                                                                        {index + 1}. {postPurchaseFeature.value}
                                                                    </bd.Flex>
                                                                    {postPurchaseFeature.link && (
                                                                        <bd.Flex gap={2} className="text-primary">
                                                                            <a
                                                                                href={postPurchaseFeature.link}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                onClick={e => e.stopPropagation()}
                                                                                style={{ textDecoration: 'underline' }}
                                                                            >
                                                                                {postPurchaseFeature.link}
                                                                            </a>
                                                                        </bd.Flex>
                                                                    )}
                                                                </bd.Flex>
                                                            }
                                                            alignTop
                                                            controls={
                                                                <bd.Button
                                                                    type="button"
                                                                    variant="icon"
                                                                    size="sm"
                                                                    color="secondary"
                                                                    onClick={() =>
                                                                        handleDeletePostPurchaseFeature(
                                                                            postPurchaseFeature.id,
                                                                            formik
                                                                        )
                                                                    }
                                                                >
                                                                    <SvgDelete />
                                                                </bd.Button>
                                                            }
                                                        />
                                                        {index <
                                                            formik.values.postPurchaseFeatures.length - 1 && (
                                                            <hr className="m-0 text-muted " />
                                                        )}
                                                    </>
                                                )
                                            )}
                                        </bd.List>
                                    </bd.Flex>
                                </bd.FormRow>
                            </>
                        )}

                        <div className="mb-3">
                            <FormikTextEditor
                                label="description"
                                name="description"
                                readOnly={!allowEdit}
                            />
                        </div>

                        <FormikFileUpload
                            imageUrl={imageUrl}
                            label="image-content"
                            name="imageContent"
                            accept="image/*"
                            readOnly={!allowEdit}
                        />

                        {showSpecificationModal && (
                            <SaveProductSpecificationModal
                                show={showSpecificationModal}
                                onHide={() => setShowSpecificationModal(false)}
                                onSave={specification =>
                                    handleSaveSpecification(specification, formik)
                                }
                                allowEdit={allowEdit}
                            />
                        )}

                        {showPostPurchaseFeatureModal && (
                            <SaveProductPostPurchaseFeatureModal
                                show={showPostPurchaseFeatureModal}
                                onHide={() => setShowPostPurchaseFeatureModal(false)}
                                onSave={postPurchaseFeature =>
                                    handleSavePostPurchaseFeature(postPurchaseFeature, formik)
                                }
                                allowEdit={allowEdit}
                            />
                        )}
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
